/**
 * OT Rule form — create and edit. Reusable for detail page in view mode later if needed.
 */
import type {
  CreateOTRuleDTO,
  OTRuleDetailDTO,
  OTRuleDayType,
  UpdateOTRuleDTO,
} from "@/modules/hr/overtime/domain/types";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  FormTextarea,
  Switch,
  toast,
  zodResolver,
} from "@devhop/ui";
import { BookmarkIcon, Sparkles } from "lucide-react";
import * as RHF from "react-hook-form";
import { useMemo, useState } from "react";
import { z } from "zod";
import { otRuleFormSamples } from "./otRuleFormSamples";

const OTRuleFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາປ້ອນຊື່ຂໍ້ກຳນົດ"),
  dayType: z.enum(["weekday", "holiday"]),
  rateMultiplier: z.number().min(0.01, "ອັດຕາຄູນຕ້ອງມີຄ່ານອກ 0"),
  effectiveFrom: z.string().min(1, "ກະລຸນາເລືອກວັນທີມີຜົນບັງຄັບໃຊ້"),
  effectiveTo: z.string(),
  isActive: z.boolean(),
  description: z.string(),
});

export type OTRuleFormValues = {
  name: string;
  dayType: OTRuleDayType;
  rateMultiplier: number;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
  description: string;
};

const DAY_TYPE_OPTIONS = [
  { value: "weekday", label: "ວັນທຳມະດາ" },
  { value: "holiday", label: "ວັນພັກ" },
];

function toInputDate(s: string): string {
  if (!s) return "";
  return s.slice(0, 10);
}

export type OTRuleFormProps =
  | {
      mode: "create";
      rule?: OTRuleDetailDTO | null;
      onSubmit: (values: CreateOTRuleDTO) => void;
      onCancel: () => void;
      isSubmitting?: boolean;
    }
  | {
      mode: "edit";
      rule?: OTRuleDetailDTO | null;
      onSubmit: (values: UpdateOTRuleDTO) => void;
      onCancel: () => void;
      isSubmitting?: boolean;
    };

export function OTRuleForm({
  mode,
  rule,
  onSubmit,
  onCancel,
  isSubmitting,
}: OTRuleFormProps) {
  const isCreate = mode === "create";

  const defaultValues: OTRuleFormValues = useMemo(() => {
    if (rule) {
      return {
        name: rule.name,
        dayType: rule.dayType,
        rateMultiplier: rule.rateMultiplier,
        effectiveFrom: toInputDate(rule.effectiveFrom),
        effectiveTo: rule.effectiveTo ? toInputDate(rule.effectiveTo) : "",
        isActive: rule.isActive,
        description: rule.description ?? "",
      };
    }
    return {
      name: "",
      dayType: "weekday",
      rateMultiplier: 1.5,
      effectiveFrom: "",
      effectiveTo: "",
      isActive: true,
      description: "",
    };
  }, [rule]);

  const [sampleIndex, setSampleIndex] = useState(0);
  const isDev =
    typeof import.meta !== "undefined" &&
    (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

  const methods = RHF.useForm<OTRuleFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(OTRuleFormSchema) as any,
    defaultValues,
    values: rule
      ? {
          name: rule.name,
          dayType: rule.dayType,
          rateMultiplier: rule.rateMultiplier,
          effectiveFrom: toInputDate(rule.effectiveFrom),
          effectiveTo: rule.effectiveTo ? toInputDate(rule.effectiveTo) : "",
          isActive: rule.isActive,
          description: rule.description ?? "",
        }
      : undefined,
  });

  const handleAutoFill = () => {
    const sample = otRuleFormSamples[sampleIndex];
    if (sample) {
      methods.setValue("name", sample.name);
      methods.setValue("dayType", sample.dayType);
      methods.setValue("rateMultiplier", sample.rateMultiplier);
      methods.setValue("effectiveFrom", sample.effectiveFrom);
      methods.setValue("effectiveTo", sample.effectiveTo);
      methods.setValue("isActive", sample.isActive);
      methods.setValue("description", sample.description);
      setSampleIndex((prev) => (prev + 1) % otRuleFormSamples.length);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາແກ້ໄຂຕາມຄວາມເໝາະສົມ.");
    }
  };

  const handleValidSubmit = (vals: OTRuleFormValues) => {
    const dto = {
      name: vals.name.trim(),
      dayType: vals.dayType,
      rateMultiplier: vals.rateMultiplier,
      effectiveFrom: vals.effectiveFrom,
      effectiveTo: vals.effectiveTo.trim() || null,
      isActive: vals.isActive,
      description: vals.description.trim() || null,
    };
    if (isCreate) {
      (onSubmit as (values: CreateOTRuleDTO) => void)(dto as CreateOTRuleDTO);
    } else {
      (onSubmit as (values: UpdateOTRuleDTO) => void)(dto as UpdateOTRuleDTO);
    }
  };

  return (
    <div className="mx-auto">
      <FormRoot<OTRuleFormValues>
        methods={methods}
        onSubmit={handleValidSubmit}
        className="w-full"
      >
        <Card className="w-full">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookmarkIcon className="h-4 w-4" />
              {isCreate ? "ສ້າງຂໍ້ກຳນົດ OT" : "ແກ້ໄຂຂໍ້ກຳນົດ OT"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormInput
              name="name"
              label="ຊື່ຂໍ້ກຳນົດ"
              placeholder="ຕົວຢ່າງ: OT ວັນທຳມະດາ 1.5x"
              requiredMark
            />
            <FormSelect
              name="dayType"
              label="ປະເພດວັນ (dayType)"
              options={DAY_TYPE_OPTIONS}
            />
            <FormNumber
              name="rateMultiplier"
              label="ອັດຕາຄູນ (rateMultiplier)"
              placeholder="1.5"
              hint="ຕົວຢ່າງ: 1.5 ຫຼື 2.0 ຕາມກົດໝາຍ"
            />
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                name="effectiveFrom"
                label="ມີຜົນຕັ້ງແຕ່ (effectiveFrom)"
                type="date"
                requiredMark
              />
              <FormInput
                name="effectiveTo"
                label="ມີຜົນຈົນເຖິງ (effectiveTo)"
                type="date"
                hint="ວ່າງ = ຖືກໃຊ້ຕໍ່ໄປ"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium text-sm">ສະຖານະ (isActive)</p>
                <p className="text-muted-foreground text-xs">
                  ເປີດ/ປິດການໃຊ້ງານຂໍ້ກຳນົດນີ້
                </p>
              </div>
              <Switch
                id="ot-rule-is-active"
                checked={methods.watch("isActive")}
                onCheckedChange={(checked) =>
                  methods.setValue("isActive", checked)
                }
              />
            </div>
            <FormTextarea
              name="description"
              label="ຄຳອະທິບາຍ (optional)"
              rows={3}
              placeholder="ອະທິບາຍເພີ່ມເຕີມ..."
            />
          </CardContent>
        </Card>
        {/* Footer — sticky so save actions stay visible when scrolling */}
        <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              ຍົກເລີກ
            </Button>
            {isDev && isCreate && otRuleFormSamples.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAutoFill}
                disabled={isSubmitting}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                ຕື່ມຕົວຢ່າງ ({sampleIndex + 1}/{otRuleFormSamples.length})
              </Button>
            )}
          </div>
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
          >
            {isCreate ? "ສ້າງ" : "ບັນທຶກ"}
          </Button>
        </div>
      </FormRoot>
    </div>
  );
}
